import React, { useEffect, useState, useRef } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import RibbonActions from '../components/RibbonActions';
import api, { getGastos, crearGasto, analizarGasto, confirmarGasto, borrarGasto, getProyectos } from '../services/api';
import FiltroPanel from '../components/FiltroPanel';
import DocHistorialModal, { ClockIcon } from '../components/DocHistorialModal';
import BuzonesModal from '../components/BuzonesModal';
import { useAuth } from '../context/AuthContext';
import { puede } from '../utils/permisos';

const HIST_BTN = { background:'#1e293b', border:'1px solid #334155', borderRadius:6, padding:'4px 8px', cursor:'pointer', color:'#93c5fd', display:'inline-flex', alignItems:'center', verticalAlign:'middle', marginRight:6 };
const IC = {
  mail: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg>,
  borrador: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  scan: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.8"/><path d="m21 15-4.5-4.5L5 21"/></svg>,
};
const iconLbl = { display: 'inline-flex', alignItems: 'center', gap: 6 };
const CATEGORIAS = ['peaje', 'combustible', 'parqueadero', 'transporte', 'viaticos', 'alimentacion', 'hospedaje', 'papeleria', 'otro'];
const CAT_LABEL = { peaje:'Peaje', combustible:'Combustible', parqueadero:'Parqueadero', transporte:'Transporte', viaticos:'Viáticos', alimentacion:'Alimentación', hospedaje:'Hospedaje', papeleria:'Papelería', otro:'Otro' };
const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-CO');
const hoy = () => new Date().toISOString().slice(0, 10);
const FORM0 = () => ({ fecha: hoy(), categoria: 'peaje', concepto: '', valor: '', iva: '', medio_pago: 'efectivo', proyecto_id: '', proveedor_nit: '', proveedor_nombre: '' });

export default function Gastos() {
  const [lista, setLista] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [verHist, setVerHist] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(FORM0());
  const [foto, setFoto] = useState(null);
  const [leyendoIA, setLeyendoIA] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [buzOpen, setBuzOpen] = useState(false);
  const fileRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFoto(file);
  };

  // Al adjuntar el escaneo: guarda el archivo (irá como soporte al crear) y lo
  // pasa por la IA para precargar fecha, valor, concepto, categoría y proveedor.
  const onFoto = async (file) => {
    setFoto(file || null);
    if (!file) return;
    setLeyendoIA(true);
    try {
      const fd = new FormData();
      fd.append('foto', file);
      const { data } = await analizarGasto(fd);
      const sug = data?.sugerido || {};
      setForm(f => ({
        ...f,
        fecha:            sug.fecha || f.fecha,
        categoria:        sug.categoria || f.categoria,
        concepto:         sug.concepto || f.concepto,
        valor:            (sug.valor !== '' && sug.valor != null) ? sug.valor : f.valor,
        iva:              (sug.iva   !== '' && sug.iva   != null) ? sug.iva   : f.iva,
        proveedor_nit:    sug.proveedor_nit || f.proveedor_nit,
        proveedor_nombre: sug.proveedor_nombre || f.proveedor_nombre,
      }));
    } catch (e) {
      // Si la IA falla, no bloqueamos: el usuario llena a mano. La imagen igual queda.
    } finally { setLeyendoIA(false); }
  };
  // Filtros
  const [cat, setCat] = useSessionState('gas_cat', '');
  const [anio, setAnio] = useSessionState('gas_anio', '');
  const [busq, setBusq] = useSessionState('gas_busq', '');

  const load = () => getGastos().then(r => setLista(r.data)).catch(() => {});
  const { user } = useAuth();
  const tieneProyectos = puede(user, 'proyectos'); // ingeniería: el gasto se liga a un proyecto solo si existe el módulo
  useEffect(() => { load(); if (tieneProyectos) getProyectos().then(r => setProyectos(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  // Abre el modal para REVISAR y CONFIRMAR un borrador (gasto que llegó por el
  // buzón de caja menor). Pre-llena con los datos leídos por IA; la imagen ya
  // está guardada (se ve con "Ver tiquete").
  const abrirConfirmar = (g) => {
    setForm({
      fecha: g.fecha ? String(g.fecha).slice(0, 10) : hoy(),
      categoria: g.categoria || 'otro',
      concepto: g.concepto || '',
      valor: g.valor ?? '',
      iva: g.iva ?? '',
      medio_pago: g.medio_pago || 'efectivo',
      proyecto_id: g.proyecto_id || '',
      proveedor_nit: g.proveedor_nit || '',
      proveedor_nombre: g.proveedor_nombre || '',
    });
    setFoto(null);
    setConfirmandoId(g.id);
    setModal(true);
  };

  const guardar = async () => {
    if (!form.valor) return alert('Ingrese el valor del gasto');
    setSaving(true);
    try {
      if (confirmandoId) {
        await confirmarGasto(confirmandoId, form);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
        if (foto) fd.append('foto', foto);
        await crearGasto(fd);
      }
      setModal(false); setForm(FORM0()); setFoto(null); setConfirmandoId(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Error al guardar el gasto'); }
    finally { setSaving(false); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar el gasto de ${fmt(g.valor)} (${CAT_LABEL[g.categoria] || g.categoria})? Se elimina también su comprobante.`)) return;
    try { await borrarGasto(g.id); load(); } catch (e) { alert(e.response?.data?.error || 'No se pudo eliminar'); }
  };

  const verFoto = async (g) => {
    try { const r = await api.get(`/gastos/${g.id}/foto`, { responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank'); }
    catch { alert('No se pudo abrir la foto'); }
  };

  const anios = [...new Set(lista.map(g => g.fecha ? new Date(g.fecha).getFullYear() : null).filter(Boolean))].sort((a, b) => b - a);
  const filtrada = lista.filter(g => {
    if (cat && g.categoria !== cat) return false;
    if (anio && (!g.fecha || String(new Date(g.fecha).getFullYear()) !== anio)) return false;
    if (busq) { const q = busq.toLowerCase(); if (!`${g.concepto || ''} ${CAT_LABEL[g.categoria] || ''} ${g.proyecto_nombre || ''}`.toLowerCase().includes(q)) return false; }
    return true;
  });
  const totalF = filtrada.reduce((a, g) => a + Number(g.valor || 0), 0);

  return (
    <div>
      <div style={s.pageHeader}>
        <div><h1 style={s.h1}>Gastos / Caja menor</h1><p style={s.sub}>Gastos de campo (peajes, combustible, viáticos…) con soporte y contabilización automática</p></div>
        <RibbonActions><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FiltroPanel
            fields={[
              { tipo: 'select', label: 'Categoría', value: cat, onChange: setCat, options: [{ value: '', label: 'Todas' }, ...CATEGORIAS.map(c => ({ value: c, label: CAT_LABEL[c] }))] },
              { tipo: 'input', label: 'Buscar', value: busq, onChange: setBusq, placeholder: 'Concepto o proyecto…' },
              { tipo: 'select', label: 'Año', value: anio, onChange: setAnio, options: [{ value: '', label: 'Todos' }, ...anios.map(a => ({ value: String(a), label: String(a) }))] },
            ]}
            onClear={() => { setCat(''); setBusq(''); setAnio(''); }}
          />
          <button style={s.btnSecondary} onClick={() => setBuzOpen(true)}><span style={iconLbl}>{IC.mail} Buzón de caja menor</span></button>
          <button style={s.btnPrimary} onClick={() => { setForm(FORM0()); setFoto(null); setConfirmandoId(null); setModal(true); }}>+ Nuevo gasto</button>
        </div></RibbonActions>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 0 12px' }}>
        <span style={{ color: '#64748b', fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>{filtrada.length} gastos · {fmt(totalF)}</span>
      </div>

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>{['Fecha', 'Categoría', 'Concepto', 'Proyecto', 'Valor', 'Comprobante', 'Soporte', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtrada.length === 0 && <tr><td colSpan={8} style={s.empty}>{lista.length ? 'Ninguno coincide con el filtro' : 'Sin gastos registrados'}</td></tr>}
            {filtrada.map(g => (
              <tr key={g.id}>
                <td style={s.td}>{g.fecha ? new Date(g.fecha).toLocaleDateString('es-CO') : '—'}</td>
                <td style={s.td}><span style={s.badge}>{CAT_LABEL[g.categoria] || g.categoria}</span></td>
                <td style={s.td}>{g.concepto || '—'}</td>
                <td style={s.td}>{g.proyecto_nombre || '—'}</td>
                <td style={{ ...s.td, fontWeight: 600 }}>{fmt(g.valor)}</td>
                <td style={{ ...s.td, color: '#93c5fd' }}>
                  {g.estado === 'borrador'
                    ? <span style={{ background: '#78350f', color: '#fcd34d', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{IC.borrador} Borrador</span>
                    : (g.comprobante_numero || '—')}
                </td>
                <td style={s.td}>{g.tiene_foto ? <button style={s.btnVer} onClick={() => verFoto(g)}>Ver tiquete</button> : <span style={{ color: '#475569' }}>—</span>}</td>
                <td style={{ ...s.td, whiteSpace:'nowrap' }}>
                  {g.estado === 'borrador' && <button style={s.btnConfirm} onClick={() => abrirConfirmar(g)}>Revisar y confirmar</button>}
                  <button style={HIST_BTN} title="Historial" onClick={() => setVerHist({ docTipo:'gasto', docId: g.id, numero: g.concepto || CAT_LABEL[g.categoria] || 'Gasto' })}><ClockIcon /></button>
                  <button style={s.btnDel} onClick={() => eliminar(g)}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 520 }}>
            <div style={s.modalHeader}><h2 style={s.modalTitle}>{confirmandoId ? 'Revisar y confirmar gasto' : 'Nuevo gasto'}</h2><button style={s.closeBtn} onClick={() => { setModal(false); setConfirmandoId(null); }}>✕</button></div>
            <div style={s.modalBody}>
              <div style={s.row2}>
                <div style={s.field}><label style={s.label}>Fecha *</label>
                  <input style={s.input} type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>Categoría *</label>
                  <select style={s.input} value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
                  </select></div>
                <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Concepto</label>
                  <input style={s.input} placeholder="Ej. Peaje Chusacá — viaje a Girardot" value={form.concepto} onChange={e => setForm({ ...form, concepto: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>NIT proveedor (para exógena)</label>
                  <input style={s.input} placeholder="900123456" value={form.proveedor_nit} onChange={e => setForm({ ...form, proveedor_nit: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>Nombre proveedor</label>
                  <input style={s.input} placeholder="Razón social / nombre" value={form.proveedor_nombre} onChange={e => setForm({ ...form, proveedor_nombre: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>Valor *</label>
                  <input style={s.input} type="number" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>IVA (si aplica)</label>
                  <input style={s.input} type="number" placeholder="0" value={form.iva} onChange={e => setForm({ ...form, iva: e.target.value })} /></div>
                <div style={s.field}><label style={s.label}>Medio de pago</label>
                  <select style={s.input} value={form.medio_pago} onChange={e => setForm({ ...form, medio_pago: e.target.value })}>
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select></div>
                {tieneProyectos && (
                <div style={s.field}><label style={s.label}>Proyecto / OT</label>
                  <select style={s.input} value={form.proyecto_id} onChange={e => setForm({ ...form, proyecto_id: e.target.value })}>
                    <option value="">(ninguno)</option>
                    {proyectos.map(p => <option key={p.id} value={p.id}>{p.codigo ? `${p.codigo} — ` : ''}{p.nombre}</option>)}
                  </select></div>
                )}
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <label style={s.label}>Foto / escaneo del tiquete {leyendoIA && <span style={{ color:'#7dd3fc', fontWeight:600 }}>· 🔎 Leyendo con IA…</span>}</label>
                  <div
                    onClick={() => !leyendoIA && fileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); if (!leyendoIA) setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    style={{
                      border: `2px dashed ${dragOver ? '#6366f1' : '#334155'}`,
                      background: dragOver ? '#1e2540' : '#0f172a',
                      borderRadius: 10, padding: '20px', textAlign: 'center',
                      cursor: leyendoIA ? 'default' : 'pointer', color: '#94a3b8', fontSize: 13,
                      lineHeight: 1.5, transition: 'all .12s',
                    }}>
                    {leyendoIA
                      ? <span style={{ color: '#7dd3fc' }}>🔎 Leyendo el tiquete con IA…</span>
                      : foto
                        ? <span style={{ color: '#86efac' }}>📎 {foto.name}<br/><span style={{ color: '#64748b', fontSize: 12 }}>clic para cambiar</span></span>
                        : <><div style={{ marginBottom: 6, color: '#64748b' }}>{IC.scan}</div>Arrastra aquí el escaneo del tiquete<br/><span style={{ color: '#64748b', fontSize: 12 }}>o haz clic para seleccionar (imagen o PDF)</span></>}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                    onChange={e => onFoto(e.target.files?.[0] || null)} />
                  <p style={{ color:'#64748b', fontSize:11, margin:'6px 0 0' }}>Al adjuntar el escaneo, la IA precarga fecha, valor, concepto y proveedor (revisa antes de guardar). La imagen queda como soporte.</p>
                </div>
              </div>
              <p style={{ color: '#64748b', fontSize: 12, marginTop: 12 }}>Al guardar se genera automáticamente el comprobante de egreso (débito al gasto, crédito {form.medio_pago === 'efectivo' ? 'caja' : 'banco'}).</p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => { setModal(false); setConfirmandoId(null); }}>Cancelar</button>
              <button style={{ ...s.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : (confirmandoId ? 'Confirmar gasto' : 'Guardar gasto')}</button>
            </div>
          </div>
        </div>
      )}
      {verHist && <DocHistorialModal {...verHist} onClose={() => setVerHist(null)} />}
      {buzOpen && <BuzonesModal soloTipo="caja_menor" onClose={() => { setBuzOpen(false); load(); }} />}
    </div>
  );
}

const s = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 },
  h1: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  sub: { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  btnPrimary: { background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  card: { background: '#1e293b', borderRadius: 12, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 800 },
  th: { padding: '12px 16px', color: '#64748b', fontSize: 12, textAlign: 'left', borderBottom: '1px solid #334155', background: '#162032', whiteSpace: 'nowrap' },
  td: { padding: '10px 16px', color: '#cbd5e1', fontSize: 13, borderBottom: '1px solid #1a2332', verticalAlign: 'middle' },
  empty: { padding: '32px', textAlign: 'center', color: '#475569' },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#1e293b', border: '1px solid #334155', color: '#93c5fd', textTransform: 'capitalize' },
  btnVer: { background: '#0f172a', border: '1px solid #334155', color: '#7dd3fc', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  btnConfirm: { background: '#166534', border: '1px solid #22c55e', color: '#dcfce7', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 },
  btnDel: { background: 'transparent', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#1e293b', borderRadius: 16, width: '95%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', border: '1px solid #334155' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' },
  modalTitle: { color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' },
  modalBody: { padding: '20px 24px', overflowY: 'auto', overflowX: 'hidden' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #334155' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '9px 11px', color: '#f1f5f9', fontSize: 13 },
};
